import React, { useEffect, useState } from "react";

import { useQuery } from "@apollo/client";
import { Avatar, Box, Tabs, Typography } from "@mui/material";
import { useLocation, Link } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { colors } from "@/theme/overrides/colors";
import Spacer from "@components/layout/Spacer";

import HeaderTab from "./HeaderTab";

interface Props {
  chatId: string;
  fromProfile: boolean;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    backgroundColor: colors.white,
  },
  nameContainer: {
    alignSelf: "flex-start",
    borderRadius: 10,
    "&:hover": {
      backgroundColor: "#F5F5F5",
      cursor: "pointer",
    },
  },
  name: {
    fontWeight: 600,
    fontSize: 18,
    lineHeight: 1,
    color: colors.dark,
  },
  tabs: {
    minHeight: 38, // tab line-height + 10 padding-top + 10 padding-bottom
  },
  tabsIndicator: {
    height: 2,
    padding: theme.spacing(0, 4),
    backgroundColor: "unset",
  },
  indicatorBg: {
    width: "100%",
    height: "100%",
    borderRadius: 1,
    backgroundColor: theme.palette.primary.main,
  },
}));

const headerQuery = graphql(`
  query ChatHeader($id: ID!) {
    chat(id: $id) {
      id
      client {
        id
        profile {
          id
          fullName
          avatarUrl(variant: THUMB)
        }
      }
      appointmentUnreadCount: viewerUnreadMessagesCount(channel: APPOINTMENT)
      mealPlanUnreadCount: viewerUnreadMessagesCount(channel: MEAL_PLAN)
      progressUnreadCount: viewerUnreadMessagesCount(channel: PROGRESS)
      questionsUnreadCount: viewerUnreadMessagesCount(channel: QUESTIONS)
    }
  }
`);

const Header: React.FC<Props> = ({ chatId, fromProfile }) => {
  const { classes } = useStyles();

  const { data } = useQuery(headerQuery, { variables: { id: chatId } });
  const chat = data?.chat;

  const location = useLocation();
  const [, , , tab, profileTab] = location.pathname.split("/");
  const tabToUse = fromProfile ? profileTab : tab;
  const [currentTab, setCurrentTab] = useState(tabToUse ?? "appointment");
  useEffect(() => {
    if (tabToUse) setCurrentTab(tabToUse);
  }, [tabToUse]);

  const unreadAppointment = chat?.appointmentUnreadCount;
  const unreadMealPlan = chat?.mealPlanUnreadCount;
  const unreadProgress = chat?.progressUnreadCount;
  const unreadQuestions = chat?.questionsUnreadCount;
  return (
    <div className={classes.root}>
      {!fromProfile && (
        <Box display="flex" padding={2} paddingBottom={0}>
          <Link
            style={{ textDecoration: "none" }}
            to={`/clients/${chat?.client?.id}/profile`}
            rel="noreferrer"
          >
            <Box
              display="flex"
              alignItems="center"
              padding={2}
              className={classes.nameContainer}
            >
              <Avatar
                alt={chat?.client?.profile?.fullName}
                src={chat?.client?.profile?.avatarUrl ?? undefined}
              />
              <Spacer x={3} />
              <Typography className={classes.name}>
                {chat?.client?.profile?.fullName}
              </Typography>
            </Box>
          </Link>
        </Box>
      )}
      <Tabs
        className={classes.tabs}
        classes={{ indicator: classes.tabsIndicator }}
        value={currentTab}
        TabIndicatorProps={{
          children: <div className={classes.indicatorBg} />, // allows us to lessen the width of the tab indicator
        }}
      >
        <HeaderTab
          to={`appointment`}
          label="Appointment"
          value="appointment"
          unreadCount={unreadAppointment}
        />
        <HeaderTab
          to={`meal-plan`}
          label="Meal plan"
          value="meal-plan"
          unreadCount={unreadMealPlan}
        />
        <HeaderTab
          to={`progress`}
          label="Progress"
          value="progress"
          unreadCount={unreadProgress}
        />
        <HeaderTab
          to={`questions`}
          label="Questions"
          value="questions"
          unreadCount={unreadQuestions}
        />
      </Tabs>
    </div>
  );
};

export default Header;
